import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { TenderCaseApi, TenderPersonRef } from "@/api/tender-api"

import {
  createTenderRun,
  deleteTenderDocument,
  getTenderCase,
  setRunOverrideDocuments,
  updateTenderCase,
  uploadTenderDocuments,
} from "@/api/tender-api"

import { apiCaseToTenderCase, TENDER_QUERY_KEYS } from "./use-tender-cases"
import {
  isActiveAutoPipelineStatus,
  isProcessingStepStatus,
} from "../components/tender-status-utils"

export function useTenderCase(caseId: string | undefined) {
  return useQuery({
    queryKey: TENDER_QUERY_KEYS.case(caseId ?? ""),
    queryFn: () => getTenderCase(caseId!),
    enabled: !!caseId,
    select: apiCaseToTenderCase,
    refetchInterval: (query) => {
      const raw = query.state.data as TenderCaseApi | undefined
      if (!raw) return false
      // Only poll the active run — inactive runs must not keep the case query hot.
      const activeRun = raw.runs.find((run) => run.id === raw.activeRunId) ?? raw.runs.at(0)
      if (!activeRun) return false
      const hasActiveProcessing =
        isActiveAutoPipelineStatus(activeRun.autoPipelineStatus) ||
        isProcessingStepStatus(activeRun.extractionStatus) ||
        isProcessingStepStatus(activeRun.cvMatchStatus) ||
        isProcessingStepStatus(activeRun.bidReviewStatus)
      if (hasActiveProcessing) return 3000
      const docs = [
        ...activeRun.procurementDocuments,
        ...activeRun.contextDocuments,
        ...activeRun.bidDocuments,
        ...activeRun.cvDocuments,
      ]
      return docs.some((d) => d.status === "uppladdad" || d.status === "indexeras") ? 3000 : false
    },
  })
}

type UpdateTenderCasePatch = {
  title?: string
  isTitleAutoGenerated?: boolean
  procuringAuthority?: string | null
  sourceDocumentsUrl?: string | null
  /** "Sista dag för frågor" (YYYY-MM-DD); null clears it. */
  questionsDeadline?: string | null
  /** Explicit null clears the intended Ansvarig; omit to leave it alone. */
  responsible?: TenderPersonRef | null
}

/** `caseId` is the case this mutate started for — not the hook arg after a switch. */
type UpdateTenderCaseVars = UpdateTenderCasePatch & { caseId: string }

export function useUpdateTenderCase(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    // A case switch must reset the observer. Same key + pending mutate lets
    // MutationObserver.setOptions rebind onSuccess onto the newly rendered case.
    mutationKey: [...TENDER_QUERY_KEYS.case(caseId), "update"] as const,
    mutationFn: ({ caseId: id, ...patch }: UpdateTenderCaseVars) => updateTenderCase(id, patch),
    onMutate: async ({ caseId: id, ...patch }) => {
      await queryClient.cancelQueries({ queryKey: TENDER_QUERY_KEYS.case(id) })
      const previous = queryClient.getQueryData<TenderCaseApi>(TENDER_QUERY_KEYS.case(id))
      // `responsible` is the request shape, not the cache shape: the cached case
      // carries responsibleUserOid / responsibleDisplayName / responsibleEmail.
      // Spreading the patch raw left the old Ansvarig on screen until the
      // round-trip finished, and wrote a stray `responsible` key into the cache.
      const { responsible, ...caseFields } = patch
      const optimistic: Partial<TenderCaseApi> =
        "responsible" in patch
          ? {
              ...caseFields,
              responsibleUserOid: responsible?.oid ?? null,
              responsibleDisplayName: responsible?.displayName ?? "",
              responsibleEmail: responsible?.email ?? "",
            }
          : caseFields
      queryClient.setQueryData<TenderCaseApi>(TENDER_QUERY_KEYS.case(id), (old) => {
        if (!old) return old
        return { ...old, ...optimistic, updatedAt: new Date().toISOString() }
      })
      return { previous }
    },
    onError: (_err, { caseId: id }, context) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(id), context?.previous)
      // The rolled-back snapshot may predate a server-side auto-join that
      // persisted before the request failed — refetch server truth.
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(id) })
    },
    onSuccess: (data, { caseId: id }) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(id), data)
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

export function useUploadTenderDocuments(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { runId: string; docType: "procurement" | "context"; files: File[] }) =>
      uploadTenderDocuments(caseId, params),
    onMutate: async ({ runId, docType, files }) => {
      await queryClient.cancelQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId) })
      const previous = queryClient.getQueryData<TenderCaseApi>(TENDER_QUERY_KEYS.case(caseId))
      queryClient.setQueryData<TenderCaseApi>(TENDER_QUERY_KEYS.case(caseId), (old) => {
        if (!old) return old
        const optimisticDocs = files.map((file) => ({
          id: crypto.randomUUID(),
          filename: file.name,
          uploadedAt: new Date().toISOString(),
          status: "uppladdad" as const,
          pages: 0,
          sizeKb: Math.round(file.size / 1024),
        }))
        return {
          ...old,
          runs: old.runs.map((run) => {
            if (run.id !== runId) return run
            return {
              ...run,
              procurementDocuments:
                docType === "procurement"
                  ? [...run.procurementDocuments, ...optimisticDocs]
                  : run.procurementDocuments,
              contextDocuments:
                docType === "context"
                  ? [...run.contextDocuments, ...optimisticDocs]
                  : run.contextDocuments,
            }
          }),
        }
      })
      return { previous }
    },
    onError: (_err, _params, context) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId), context?.previous)
      // The rolled-back snapshot may predate a server-side auto-join that
      // persisted before the request failed — refetch server truth.
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId) })
    },
    onSuccess: (data) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId), data)
    },
  })
}

export function useCreateTenderRun(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sourceRunId: string) => createTenderRun(caseId, sourceRunId),
    onSuccess: (data) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId), data)
    },
    onError: () => {
      // The request may have auto-joined the caller before failing.
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId) })
    },
  })
}

export function useDeleteTenderDocument(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ docId, runId }: { docId: string; runId: string }) =>
      deleteTenderDocument(caseId, docId, runId),
    onSuccess: (data) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId), data)
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId) })
    },
  })
}

export function useSetRunOverrideDocuments(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      runId,
      overrideDocumentIds,
    }: {
      runId: string
      overrideDocumentIds: string[]
    }) => setRunOverrideDocuments(caseId, runId, overrideDocumentIds),
    onMutate: async ({ runId, overrideDocumentIds }) => {
      await queryClient.cancelQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId) })
      const previous = queryClient.getQueryData(TENDER_QUERY_KEYS.case(caseId))
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId), (old: TenderCaseApi | undefined) => {
        if (!old) return old
        return {
          ...old,
          runs: old.runs.map((run) => {
            if (run.id !== runId) return run
            return { ...run, overrideDocumentIds }
          }),
        }
      })
      return { previous }
    },
    onError: (_err, _params, context) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId), context?.previous)
      // The rolled-back snapshot may predate a server-side auto-join that
      // persisted before the request failed — refetch server truth.
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(caseId) })
    },
    onSuccess: (data) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.case(caseId), data)
    },
  })
}
